import {
  AlertOutlined,
  AppstoreOutlined,
  BankOutlined,
  CarOutlined,
  ContainerOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  ExperimentOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  MoneyCollectOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { UserMenu } from '@hq/account';
import { hasPermission } from '@hq/permission';
import { truncateByWidth } from '@hq/utils';
import { Button, Drawer, Layout, Menu, Space, Tag, Typography, type MenuProps } from 'antd';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../components/useMobile';
import { appTitle, hq, levelLabel, useAuthStore } from '../session';

const { Header, Sider, Content } = Layout;

/** 运营端菜单项：带 children 的是二级分组（分组本身不跳转）。 */
interface MenuItem {
  key: string;
  icon?: ReactNode;
  label: string;
  /** 进入该菜单所需权限码（sys_permission.perm_code）；空表示登录即可见。 */
  perm?: string;
  /** 多个权限码满足任一即可（与后端 `HasPerm(a) || HasPerm(b)` 的口径对齐）。 */
  perms?: string[];
  children?: MenuItem[];
}

/** 新窗口打开的全屏大屏路由（不走账号登录态，凭屏端设备凭据独立取数）；非真实路径，不参与选中匹配。 */
const SCREEN_EXT_KEY = 'ext:/screen';

/**
 * 运营端菜单（docs/05 §4.2）：一级 14 项（<16），按「权限码 + 功能集合」聚出二级分组。
 * 排序按使用频率与履约链路：看板 → 接单 → WMS → 采购 → 缺货 → AI → 库存 → 配送 → 客服 → 结算 → 大屏 → 机构 → 用户 → 参数审计。
 */
const MENU: MenuItem[] = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '运营看板' },
  // 后端 OrderCenter.Accept/Reject 强制 wl:purchase:manage，菜单保持同口径
  { key: '/order', icon: <AppstoreOutlined />, label: '接单中心', perm: 'wl:purchase:manage' },
  {
    // SupplyOrder 出库确认接受 wl:purchase:manage 或 wl:inventory:manage
    key: 'g:wms',
    icon: <ContainerOutlined />,
    label: '外部仓 WMS',
    perms: ['wl:inventory:manage', 'wl:purchase:manage'],
    children: [
      { key: '/wms', label: '仓库概览' },
      { key: '/wms/ops', label: '仓内作业' },
      { key: '/wms/stock', label: '货位库存' }
    ]
  },
  {
    key: 'g:purchase',
    icon: <ShoppingCartOutlined />,
    label: '采购中心',
    perm: 'wl:purchase:manage',
    children: [
      { key: '/purchase', label: '采购概览' },
      { key: '/purchase/inquiry', label: '询价比价' },
      { key: '/purchase/contract', label: '合同台账' },
      { key: '/purchase/supplier', label: '供应商协同' },
      { key: '/purchase/price-change', label: '调价申请' },
      { key: '/purchase/material', label: '物资主数据' },
      { key: '/purchase/free-assets', label: '赠品资产台账' },
      { key: '/purchase/stock-rule', label: '库存规则' },
      { key: '/substitute', label: '替代推荐' }
    ]
  },
  { key: '/shortage', icon: <AlertOutlined />, label: '缺货处理', perm: 'wl:purchase:manage' },
  // AI 预测/推荐写接口强制 wl:purchase:manage，依据反查需 wl:analytics:view
  { key: '/ai', icon: <ExperimentOutlined />, label: 'AI 中台', perm: 'wl:purchase:manage' },
  {
    // 采购收货只生成 WAIT_CHECK 入库单，必须在此确认才加库存
    key: 'g:inventory',
    icon: <DatabaseOutlined />,
    label: '库存管理',
    perms: ['wl:inventory:manage', 'wl:purchase:manage'],
    children: [
      { key: '/inventory/alert', label: '库存预警' },
      { key: '/inventory/inbound', label: '入库单' }
    ]
  },
  {
    key: 'g:delivery',
    icon: <CarOutlined />,
    label: '配送管理',
    children: [
      { key: '/delivery', label: '配送管理' },
      { key: '/delivery/resource', label: '配送资源', perm: 'wl:delivery:manage' },
      { key: '/delivery/perf', label: '人效排行', perm: 'wl:analytics:view' },
      { key: '/delivery/wave', label: '波次作业', perm: 'wl:delivery:manage' }
    ]
  },
  {
    // ServiceMessage 以 STAFF 身份回复需 sv:service:handle
    key: 'g:service',
    icon: <CustomerServiceOutlined />,
    label: '客服中心',
    perm: 'sv:service:handle',
    children: [
      { key: '/service', label: '客服中心' },
      { key: '/service/workorder', label: '售后工单' },
      { key: '/service/satisfaction', label: '满意度评价' }
    ]
  },
  {
    key: 'g:settle',
    icon: <MoneyCollectOutlined />,
    label: '结算中心',
    perm: 'wl:settle:manage',
    children: [
      { key: '/settle', label: '结算中心' },
      { key: '/settle/service-fee', label: '服务费规则' },
      { key: '/settle/budget', label: '预算管理' }
    ]
  },
  {
    // 大屏：全屏展示（新窗口）+ 设备总表（可吊销）+ 申请码确认（输入屏上 4-4-4 三段申请码）
    key: 'g:screen',
    icon: <DesktopOutlined />,
    label: '大屏管理',
    perm: 'sys:config:manage',
    children: [
      { key: SCREEN_EXT_KEY, label: '打开大屏' },
      { key: '/system/screen-devices', label: '大屏设备' },
      { key: '/system/screen-scan', label: '申请码确认' }
    ]
  },
  {
    key: 'g:org',
    icon: <BankOutlined />,
    label: '机构与空间',
    perm: 'sys:org:manage',
    children: [
      { key: '/system/orgs', label: '机构管理' },
      { key: '/system/org-groups', label: '医共体' },
      { key: '/system/org-structure', label: '仓库与科室' },
      { key: '/system/org-space', label: '楼宇与楼层' }
    ]
  },
  {
    key: 'g:user',
    icon: <TeamOutlined />,
    label: '用户与审批',
    children: [
      { key: '/system/users', label: '用户权限', perm: 'sys:user:manage' },
      { key: '/system/approval', label: '审批流配置', perm: 'sys:approval:manage' }
    ]
  },
  {
    key: 'g:setting',
    icon: <SettingOutlined />,
    label: '参数与审计',
    children: [
      { key: '/system/org-config', label: '机构参数', perm: 'sys:config:manage' },
      { key: '/system/dict', label: '数据字典', perm: 'sys:config:manage' },
      { key: '/system/rf-devices', label: 'RF 终端设备', perm: 'sys:config:manage' },
      { key: '/system/audit', label: '审计日志', perm: 'sys:audit:view' },
      { key: '/system/interface-log', label: '接口日志', perm: 'sys:audit:view' }
    ]
  }
];

interface PermContext {
  level?: string;
  perms?: readonly string[];
}

/** 递归按权限裁剪：分组节点子项全被裁掉时整组隐藏（fail-closed）。 */
function visibleTree(items: readonly MenuItem[], ctx: PermContext): MenuItem[] {
  return items.flatMap((item) => {
    const required = item.perms?.length ? item.perms : item.perm;
    const allowed =
      !required || ctx.level === 'admin' || hasPermission(ctx.perms, required, item.perms?.length ? 'any' : 'all');
    if (!allowed) return [];
    if (!item.children?.length) return [item];
    const children = visibleTree(item.children, ctx);
    return children.length ? [{ ...item, children }] : [];
  });
}

/** antd Menu 只认 key/icon/label/children，交出去前剥掉权限字段。 */
function toMenuItems(items: readonly MenuItem[]): MenuProps['items'] {
  return items.map(({ key, icon, label, children }) =>
    children?.length ? { key, icon, label, children: toMenuItems(children) } : { key, icon, label }
  );
}

interface LeafItem {
  key: string;
  label: string;
  /** 所属分组 key 链，用于自动展开对应的二级菜单。 */
  parents: string[];
}

/** 打平成可跳转的叶子项，并保留父级链（外部入口项不参与路径匹配/标题）。 */
function collectLeaves(items: readonly MenuItem[], parents: string[] = []): LeafItem[] {
  return items.flatMap((item) =>
    item.children?.length
      ? collectLeaves(item.children, [...parents, item.key])
      : item.key.startsWith('ext:')
        ? []
        : [{ key: item.key, label: item.label, parents }]
  );
}

/** 精确命中优先，其次取最长前缀（/purchase/supplier 不被 /purchase 抢走）。 */
function matchLeaf(leaves: readonly LeafItem[], pathname: string): LeafItem | undefined {
  const exact = leaves.find((leaf) => leaf.key === pathname);
  if (exact) return exact;
  return leaves
    .filter((leaf) => pathname.startsWith(`${leaf.key}/`))
    .sort((a, b) => b.key.length - a.key.length)[0];
}

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useAuthStore((state) => state.profile);
  const clear = useAuthStore((state) => state.clear);
  const patchProfile = useAuthStore((state) => state.patchProfile);
  // 侧栏受控折叠：底部用简化图标按钮替代默认整条 trigger
  const [collapsed, setCollapsed] = useState(false);
  // 移动端：<768px 侧栏改为抽屉，菜单选中后自动收起
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 菜单按后端权限码裁剪：管理员不裁剪；无权限快照的会话按 fail-closed 处理（只显示无需权限的菜单）
  const menu = useMemo(() => {
    const tree = visibleTree(MENU, { level: profile?.level, perms: profile?.perms });
    return { items: toMenuItems(tree), leaves: collectLeaves(tree) };
  }, [profile?.level, profile?.perms]);

  // 页签标题从 MENU 单一来源派生，避免各页面手写 document.title 漂移
  useEffect(() => {
    const current = matchLeaf(menu.leaves, location.pathname);
    document.title = current ? `${current.label} · ${appTitle}` : appTitle;
  }, [location.pathname, menu.leaves]);

  // 子页面高亮所属菜单：先精确匹配，再取最长前缀（/purchase/supplier 不能被 /purchase 抢走）
  const selected = matchLeaf(menu.leaves, location.pathname)?.key ?? menu.leaves[0]?.key ?? '';

  // 二级菜单展开状态（手风琴）：同一时刻只展开一个分组
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  // 路由变化（含点击二级菜单）时只保留当前页所属分组，其它已展开的分组自动收起
  useEffect(() => {
    setOpenKeys(matchLeaf(menu.leaves, location.pathname)?.parents ?? []);
  }, [location.pathname, menu.leaves]);

  // 手动点开某个分组时收起其它分组；关闭分组时保留仍然展开的那个（再次点击即全部收起）
  const handleOpenChange = (keys: string[]) => {
    setOpenKeys((prev) => {
      const opened = keys.find((key) => !prev.includes(key));
      return opened ? [opened] : keys.slice(-1);
    });
  };

  // 移动端切换菜单后自动收起抽屉
  useEffect(() => {
    if (isMobile) setDrawerOpen(false);
  }, [selected, isMobile]);

  // 侧栏内容（logo + 菜单）：桌面放 Sider，移动端放 Drawer，两端复用同一份
  const siderContent = (
    <>
      <div className="app-logo">
        <img className="app-logo__img" src="/logo.png" alt="logo" />
        {!collapsed && <span title={appTitle}>{truncateByWidth(appTitle)}</span>}
      </div>
      <div className="app-sider__menu">
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selected]}
          // 折叠态由 antd 用悬浮气泡接管展开，传 openKeys 会与之打架
          {...(collapsed && !isMobile ? {} : { openKeys, onOpenChange: handleOpenChange })}
          items={menu.items}
          onClick={({ key }) => (key === SCREEN_EXT_KEY ? window.open('/screen', '_blank') : navigate(key))}
        />
      </div>
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          width={232}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          closable={false}
          classNames={{ body: 'app-drawer__body' }}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          {siderContent}
        </Drawer>
      ) : (
        <Sider
          className="app-sider"
          theme="dark"
          width={208}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{ position: 'sticky', top: 0, height: '100vh' }}
        >
          {siderContent}
        </Sider>
      )}
      <Layout>
        <Header
          style={{
            background: '#fff',
            paddingInline: isMobile ? 12 : 24,
            height: 56,
            // 不能用 56px 行高：antd ellipsis 单行文本是 inline-block + vertical-align: bottom，
            // 会被 56px 行盒压到最底部，姓名与「退出」就错行了；行高交回 normal 由 flex 居中
            lineHeight: 'normal',
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 16,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Space>
            {/* 收起/展开：置顶栏左侧，贴近导航栏；移动端改为抽屉开关 */}
            <Button
              type="text"
              style={{ marginInlineStart: -12 }}
              icon={isMobile ? <MenuOutlined /> : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => (isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed))}
            />
            {!isMobile && <Typography.Text type="secondary">运营商端 · 履约与结算</Typography.Text>}
            <Tag color="blue">OPS 门户</Tag>
          </Space>
          <Space size={isMobile ? 8 : 16} style={{ flexShrink: isMobile ? 1 : 0, minWidth: 0, whiteSpace: 'nowrap' }}>
            {profile ? (
              <UserMenu
                hq={hq}
                compact={isMobile}
                subtitle={profile.orgName ?? undefined}
                profile={{
                  userId: profile.userId,
                  userCode: profile.userCode,
                  name: profile.name,
                  orgName: profile.orgName,
                  deptName: profile.deptName,
                  mobile: profile.mobile,
                  email: profile.email,
                  avatarUrl: profile.avatarUrl,
                  level: profile.level,
                  levelText: levelLabel(profile.level)
                }}
                onProfileUpdated={(next) =>
                  patchProfile({
                    avatarUrl: next.avatarUrl,
                    deptName: next.deptName,
                    mobile: next.mobile,
                    email: next.email,
                    userCode: next.userCode,
                    orgName: next.orgName ?? profile.orgName
                  })
                }
                onLogout={() => {
                  clear();
                  navigate('/login', { replace: true });
                }}
              />
            ) : (
              <Typography.Text type="secondary">未登录</Typography.Text>
            )}
          </Space>
        </Header>
        <Content style={{ padding: isMobile ? 12 : 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
